/**
 * The Flashroom brand: the mark (`BrandGlyph`, the wine jewel tile with the brass ledger ribbon and
 * flag, matching `public/flashroom-mark.svg`; a unique gradient id per instance via useId so several
 * on a page do not clash) and the wordmark (`BrandWord`). The top bar sets them side by side.
 */
import { useId } from "react";

export function BrandGlyph({ size = 28, className }: { size?: number; className?: string }) {
  const g = `${useId().replace(/:/g, "")}fr`;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8e2b43" />
          <stop offset="1" stopColor="#57172a" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="9" fill={`url(#${g})`} />
      <path
        d="M10.2 25V11.9a1.4 1.4 0 0 1 .95-1.33l7.3-2.44A1.4 1.4 0 0 1 20.3 9.46V25z"
        fill="none"
        stroke="#f6d9df"
        strokeWidth="2.8"
        strokeLinejoin="round"
      />
      <path d="M21.9 14.5 27 25h-5.1z" fill="#f6d9df" opacity="0.78" />
    </svg>
  );
}

const ROOM_O =
  "M326 -12Q245 -12 178.5 25.0Q112 62 72.5 126.5Q33 191 33 273Q33 356 72.5 420.0Q112 484 178.5 521.0Q245 558 326 558Q407 558 473.0 521.0Q539 484 578.5 420.0Q618 356 618 273Q618 191 578.5 126.5Q539 62 473.0 25.0Q407 -12 326 -12ZM326 123Q387 123 425.0 165.0Q463 207 463 273Q463 338 425.0 380.5Q387 423 326 423Q265 423 226.5 380.5Q188 338 188 273Q188 207 226.5 165.0Q265 123 326 123Z";

/**
 * The wordmark, drawn from the suite master: Plus Jakarta Sans ExtraBold, the f and l apart and the
 * f's crossbar in the text colour, "room" in the accent. Inline on the text's baseline and sized in
 * em, so it stands where the old serif text stood: the fl as tall as its l was.
 */
export function BrandWord() {
  return (
    <svg
      viewBox="29 0 4715 769"
      width="4.578em"
      height="0.747em"
      style={{ display: "inline", verticalAlign: "-0.012em" }}
      role="img"
      aria-label="flashroom"
    >
      <g transform="translate(0 757) scale(1 -1)">
        <g fill="currentColor">
          <path d="M121 0H271V554Q271 627 356 627H389V757H336Q234 757 178 704Q121 651 121 554Z" />
          <path d="M29 307.5H388V437.5H29Z" />
          <path d="M435 0H585V757H435Z" />
          <path
            transform="translate(610 0)"
            d="M224 -12Q136 -12 84.5 30.0Q33 72 33 146Q33 215 81.0 260.0Q129 305 227 321L378 345V360Q378 391 353.5 412.0Q329 433 287 433Q247 433 216.5 411.0Q186 389 172 353L52 410Q76 478 141.5 518.0Q207 558 294 558Q363 558 416.0 533.0Q469 508 498.5 463.5Q528 419 528 360V0H388V51Q326 -12 224 -12ZM188 151Q188 126 207.0 112.0Q226 98 254 98Q310 98 344.0 133.0Q378 168 378 218V235L254 213Q222 207 205.0 192.5Q188 178 188 151Z"
          />
          <path
            transform="translate(1163 0)"
            d="M269 -12Q179 -12 112.5 30.5Q46 73 22 145L132 197Q153 153 189.0 128.0Q225 103 269 103Q335 103 335 152Q335 185 281 200L188 226Q121 245 85.0 287.5Q49 330 49 388Q49 439 75.0 477.0Q101 515 148.0 536.5Q195 558 256 558Q336 558 396.5 520.5Q457 483 482 415L371 363Q359 397 327.5 417.5Q296 438 256 438Q227 438 210.5 426.0Q194 414 194 393Q194 361 253 344L340 318Q408 298 444.0 257.5Q480 217 480 158Q480 82 422.0 35.0Q364 -12 269 -12Z"
          />
          <path
            transform="translate(1648 0)"
            d="M55 0V757H205V495Q228 527 265.0 542.5Q302 558 348 558Q410 558 456.5 532.0Q503 506 529.0 459.5Q555 413 555 351V0H405V319Q405 366 377.0 394.5Q349 423 305 423Q261 423 233.0 394.5Q205 366 205 319V0Z"
          />
        </g>
        <g style={{ fill: "var(--accent-text)" }}>
          <path
            transform="translate(2217 0)"
            d="M55 0V546H195V464Q216 515 254.5 533.5Q293 552 343 552H375V422H328Q273 422 239.0 388.5Q205 355 205 294V0Z"
          />
          <path transform="translate(2573 0)" d={ROOM_O} />
          <path transform="translate(3194 0)" d={ROOM_O} />
          <path
            transform="translate(3815 0)"
            d="M55 0V546H195V479Q216 519 256.0 538.5Q296 558 348 558Q402 558 446.5 535.5Q491 513 514 474Q543 517 586.5 537.5Q630 558 683 558Q742 558 787.5 532.0Q833 506 859.0 459.5Q885 413 885 351V0H735V319Q735 366 709.5 394.5Q684 423 640 423Q596 423 570.5 394.5Q545 366 545 319V0H395V319Q395 366 369.5 394.5Q344 423 300 423Q256 423 230.5 394.5Q205 366 205 319V0Z"
          />
        </g>
      </g>
    </svg>
  );
}
